<template>
  <div v-on="$listeners"
       :style="containerStyle">
    <div ref="graph" />
  </div>
</template>

<script>
  /*
  https://github.com/vasturiano/3d-force-graph
   */
  import ForceGraph from 'force-graph'
  import ForceGraph3d from '3d-force-graph'
  import { forceManyBody } from 'd3-force-3d'
  import { forceSimulation } from 'd3-force-3d'
  import { mapGetters } from 'vuex'
  import { ClickCounter } from '@js/interface-utils'
  import { FontStyle } from '@js/styles'
  import { Quaternion, Vector3 } from 'three'
  import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';

  function getValue(obj, key, defaultValue) {
    return obj ? (obj[key] ? obj[key] : defaultValue) : defaultValue;
  }

  function isObject(value) {
    return typeof value === 'object';
  }

  function getStyleValue(isObjectFlags, style, newStyle, key, state) {
    return isObjectFlags[key] ? (newStyle[key] ? (newStyle[key][state] ? newStyle[key][state] : (style[key] ? style[key] : null)) : (style[key] ? style[key] : null)) : (style[key] ? style[key] : null);
  }

  export default {
    //Eg.)
    //nodes: [{ id: 1, name: 'Python', years: 3, neighbors: [2, 3, 5,], }, ...]
    //links: [{ source: 1, target: 2, }, ...]
    name: 'BaseGraph',
    inheritAttrs: false,
    props: {
      backgroundColor: { type: String, required: false, default: 'rgba(255, 255, 255, 0.0)' },
      containerStyle: { type: Object, required: false, default: () => {}},
      disableAutoCameraOrbit: { type: Boolean, required: false, default: false },
      disableDoubleClickFitView: { type: Boolean, required: false, default: false },
      disablePopover: { type: Boolean, required: false, default: false },
      extraIndex: { type: String, required: false, default: '0' },
      graphStyle: { type: Object, required: false, default: () => {}},
      height: { type: Number, required: false, default: 480 },
      hideLabel: { type: Boolean, required: false, default: false },
      is3d: { type: Boolean, required: false, default: false },
      isHighlighted: { type: Function, required: false, default: (node) => { return false; } },
      links: { type: Array, required: true },
      nodes: { type: Array, required: true },
      popover: { type: Function, required: false, default: node => {
        return String.raw`<div>${node.id}: ${node.name}</div>`;
      }},
      styleAttributes: { type: Array, required: false, default: () => [] },
      triggerEdgeUpdates: { type: Boolean, required: false, default: false },
      triggerFitView: { type: Boolean, required: false, default: false },
      triggerNodeUpdates: { type: Boolean, required: false, default: false },
      width: { type: Number, required: false, default: 640 },
    },
    data: function () {
      return {
        autoCameraOrbitAngleStep: getValue(this.graphStyle, 'autoCameraOrbitAngleStep', 0.01),
        autoCameraOrbitIntervalId: null,
        bloomStrength: getValue(this.graphStyle, 'bloomStrength', 3),
        bloomRadius: getValue(this.graphStyle, 'bloomRadius', 1),
        bloomThreshold: getValue(this.graphStyle, 'bloomThreshold', 0.1),
        clickCounter: new ClickCounter(['background', 'node']),
        enableBloom: getValue(this.graphStyle, 'enableBloom', false),
        cooldownTicks: getValue(this.graphStyle, 'cooldownTicks', 12),
        cursorOnHover: getValue(this.graphStyle, 'cursorOnHover', 'pointer'),
        distanceMax: getValue(this.graphStyle, 'distanceMax', 60),
        fitDuration: getValue(this.graphStyle, 'fitDuration', 1000),
        fitPadding: getValue(this.graphStyle, 'fitPadding', 40),
        fontColor: getValue(this.graphStyle, 'fontColor', null),
        fontColorOnHover: getValue(this.graphStyle, 'fontColorOnHover', null),
        fontFamily: getValue(this.graphStyle, 'fontFamily', 'sans-serif'),
        fontSize: getValue(this.graphStyle, 'fontSize', '12px'),
        fontWeight: getValue(this.graphStyle, 'fontWeight', 'normal'),
        hoverWithNeighbors: getValue(this.graphStyle, 'hoverWithNeighbors', false),
        initCooldownTicks: getValue(this.graphStyle, 'initCooldownTicks', 0),
        initWarmupTicks: getValue(this.graphStyle, 'initWarmupTicks', 15),
        isObjectFlags: {},
        labelMargin: getValue(this.graphStyle, 'labelMargin', 1),
        linkWidth: getValue(this.graphStyle, 'linkWidth', 1),
        linkHoverPrecision: getValue(this.graphStyle, 'linkHoverPrecision', 40),
        neighborsOnHover: [],
        nodeColor: getValue(this.graphStyle, 'nodeColor', '#E0E0E0'),
        nodeId: getValue(this.graphStyle, 'nodeId', 'id'),
        nodeOnHover: null,
        nodeRadius: getValue(this.graphStyle, 'nodeRadius', 10),
        ringColor: getValue(this.graphStyle, 'ringColor', null),
        ringWidth: getValue(this.graphStyle, 'ringWidth', 0),
        warmupTicks: getValue(this.graphStyle, 'warmupTicks', 0),
        graph: null,
        nodeStyles: {
          fontColor: getValue(this.graphStyle, 'fontColor', null),
          fontSize: getValue(this.graphStyle, 'fontSize', '12px'),
          fontWeight: getValue(this.graphStyle, 'fontWeight', 'normal'),
          nodeColor: getValue(this.graphStyle, 'nodeColor', '#E0E0E0'),
          ringColor: getValue(this.graphStyle, 'ringColor', null),
          ringWidth: getValue(this.graphStyle, 'ringWidth', 0),
        },
        nodeStyleKeys: [],
      };
    },
    created: function () {
      this.nodeStyleKeys = Object.keys(this.nodeStyles);
      this.isObjectFlags = this.nodeStyleKeys.reduce((result, elem) => {
        result[elem] = isObject(this.nodeStyles[elem]);
        return result;
      }, {});
    },
    mounted: function () {
      this.initGraph();
      this.setBloom();
      setTimeout(() => {
        this.startAutoCameraOrbit();
      }, this.fitDuration * 2);
    },
    computed: {
      ...mapGetters('colorTheme', ['color']),
      graphData: function () {
        return {
          nodes: this.nodes,
          links: this.links,
        };
      },
    },
    methods: {
      fitGraphToCanvas: function () {
        this.graph.zoomToFit(this.fitDuration, this.fitPadding);
      },
      initGraph: function () {
        this.graph = null;
        const forceGraph = this.is3d ? ForceGraph3d : ForceGraph;

        this.graph = forceGraph()(this.$refs.graph)
          .graphData(this.graphData)
          .width(this.width)
          .height(this.height)
          .d3Force('charge', forceManyBody().distanceMax(this.distanceMax))
          .warmupTicks(this.initWarmupTicks)
          .cooldownTicks(this.initCooldownTicks)
          .nodeColor(node => 'red')
          .nodeLabel(this.disablePopover ? null : this.popover)
          .nodeId(this.nodeId)
          .nodeRelSize(this.nodeRadius)
          .linkWidth(this.linkWidth)
          .linkHoverPrecision(40)
          .onNodeHover(this.onNodeHover)
          .onNodeClick(this.onNodeClick)
          .onNodeRightClick(this.onNodeRightClick)
          .onLinkRightClick(this.onLinkRightClick)
          .onBackgroundClick(this.onBackgroundClick)
          .onBackgroundRightClick(this.onBackgroundRightClick)
          .onEngineTick(this.onEngineTick)
          .onEngineStop(() => {
            this.onEngineTick();
            this.fitGraphToCanvas();
        });

        setTimeout(() => {
          this.graph.warmupTicks(this.warmupTicks);
          this.graph.cooldownTicks(this.cooldownTicks);
          this.graph.onEngineStop(() => {});
        }, 1000);
      },
      isOnHover: function (node) {
        return node === this.nodeOnHover;
      },
      isOnHoverNeighbor: function (node) {
        return this.neighborsOnHover ? this.neighborsOnHover.includes(node.id) : false;
      },
      onBackgroundClick: function () {
        this.clickCounter.click('background');
        if (this.clickCounter.isDoubleClicked('background', false)) {
          this.fitGraphToCanvas();
        }
      },
      onEdgeUpdate: function () {
        this.graph.graphData(this.graphData);
      },
      onEngineTick: function () {
      },
      onNodeClick: function (node) {
        this.$emit('click-node', node);
        this.clickCounter.click('node');
        if (this.clickCounter.isDoubleClicked('node')) {
          this.$emit('double-click-node', node);
        }
      },
      onNodeHover: function (node) {
        this.$refs.graph.style.cursor = node ? this.cursorOnHover : null;
        this.nodeOnHover = null;
        this.neighborsOnHover = [];

        if (node) {
          this.nodeOnHover = node;
          this.neighborsOnHover = node.neighbors;
        }
      },
      onNodeUpdate: function () {
        this.graph.graphData(this.graphData)
          .warmupTicks(this.initWarmupTicks)
          .cooldownTicks(this.initCooldownTicks)
          .onEngineStop(() => {
            this.onEngineTick();
            this.fitGraphToCanvas();
          });

        setTimeout(() => {
          this.graph.warmupTicks(this.warmupTicks);
          this.graph.cooldownTicks(this.cooldownTicks);
          this.graph.onEngineStop(() => {});
        }, 1000);
      },
      setBloom: function () {
        if (this.enableBloom) {
          const bloomPass = new UnrealBloomPass();
          bloomPass.strength = this.bloomStrength;
          bloomPass.radius = this.bloomRadius;
          bloomPass.threshold = this.bloomThreshold;
          this.graph.postProcessingComposer().addPass(bloomPass);
        } else {
          this.graph.postProcessingComposer();
        }
      },
      startAutoCameraOrbit: function () {
        clearInterval(this.autoCameraOrbitIntervalId);
        this.autoCameraOrbitIntervalId = setInterval(() => {
          const up = this.graph.camera().up;
          let rotationAxis = new Vector3(up.x, up.y, up.z);
          rotationAxis.normalize();
          let rotation = new Quaternion();
          rotation.setFromAxisAngle(rotationAxis, this.autoCameraOrbitAngleStep);

          const pos = this.graph.camera().position;
          let posVector = new Vector3(pos.x, pos.y, pos.z);
          posVector.applyQuaternion(rotation);

          this.graph.cameraPosition({
            x: posVector.x,
            y: posVector.y,
            z: posVector.z,
          });
        }, 30);
      },
      stopAutoCamerOrbit: function () {
        clearInterval(this.autoCameraOrbitIntervalId);
      },
    },
    watch: {
      disableAutoCameraOrbit: function (newVal, oldVal) {
        if (newVal) {
          this.stopAutoCamerOrbit();
        } else {
          this.startAutoCameraOrbit();
        }
      },
      disablePopover (newVal, oldVal) {
        this.graph.nodeLabel(newVal ? null : this.popover);
      },
      edges: function (newEdge, oldEdge) {
        this.onEdgeUpdate();
      },
      nodes: function (newNode, oldNode) {
        this.onNodeUpdate();
      },
      triggerEdgeUpdates: function (newVal, oldVal) {
        this.onEdgeUpdate();
      },
      triggerFitView: function (newVal, oldVal) {
        this.fitGraphToCanvas();
      },
      triggerNodeUpdates: function (newVal, oldVal) {
        this.onNodeUpdate();
      },
      height: function (newVal, oldVal) {
        this.graph.height(newVal);
      },
      width: function (newVal, oldVal) {
        this.graph.width(newVal);
      },
    },
  };
</script>
